// apps/standalone-web/src/pages/DiscoveryHubDemo.tsx

import React, { useState, useCallback } from 'react';
import { 
  DiscoveryHub, 
  BaselineVsEnhanced, 
  RecommendationLogic,
  PrintedPresentation,
  type PhysicsData,
  type TopologyData,
  type EngineOutput,
  type EnhancedRecommendation,
} from '@system-rec/decision-ui';

/**
 * Demo page showcasing the new UI-Driven Reductive Strategy components:
 * - Phase 1: Discovery Hub (Physics Gate + Topology Mapper)
 * - Phase 2: Baseline vs Enhanced comparison
 * - Phase 3: Recommendation Logic UI-Shim + Printed Presentation
 */
const DiscoveryHubDemo: React.FC = () => {
  const [physicsData, setPhysicsData] = useState<PhysicsData | null>(null);
  const [topologyData, setTopologyData] = useState<TopologyData | null>(null);
  const [selectedOption, setSelectedOption] = useState<'baseline' | 'enhanced' | undefined>();
  const [enhancedRec, setEnhancedRec] = useState<EnhancedRecommendation | null>(null);
  
  // Memoize callbacks to prevent infinite loops
  const handlePhysicsUpdate = useCallback((data: PhysicsData) => {
    setPhysicsData(data);
  }, []);

  const handleTopologyUpdate = useCallback((data: TopologyData) => {
    setTopologyData(data);
  }, []);

  const handleEnhancedUpdate = useCallback((enhanced: EnhancedRecommendation) => {
    setEnhancedRec(enhanced);
  }, []);
  
  // Sample engine output for demonstration
  const sampleEngineOutput: EngineOutput = {
    systemType: 'System Boiler',
    boilerName: 'Viessmann Vitodens 100-W 19kW',
    efficiency: 0.94,
    confidenceScore: 75,
    heatLossKw: 18,
    propertySize: 120,
    includesFilter: false,
    includesFlush: false,
    includesWeatherComp: false,
    includesZoning: false,
  };

  return (
    <div style={{ 
      minHeight: '100vh',
      backgroundColor: '#f0f2f5',
      padding: '2rem',
    }}>
      {/* Page Header */}
      <div style={{ 
        maxWidth: '1400px',
        margin: '0 auto 3rem auto',
        textAlign: 'center',
      }}>
        <h1 style={{ 
          fontSize: '2.5rem',
          fontWeight: 700,
          marginBottom: '1rem',
          color: '#212529',
        }}>
          UI-Driven Reductive Strategy Demo
        </h1>
        <p style={{ 
          fontSize: '1.125rem',
          color: '#6c757d',
          maxWidth: '800px',
          margin: '0 auto',
          lineHeight: '1.6',
        }}>
          This demo showcases the three-phase implementation: Discovery Hub, 
          Baseline vs Enhanced comparison, and the Recommendation Logic UI-Shim.
        </p>
      </div>

      {/* Phase 1: Discovery Hub */}
      <section style={{ 
        maxWidth: '1400px',
        margin: '0 auto 3rem auto',
      }}>
        <div style={{ 
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ 
              fontSize: '1.75rem',
              fontWeight: 700,
              marginBottom: '0.5rem',
              color: '#212529',
            }}>
              Phase 1: Discovery Hub
            </h2>
            <p style={{ 
              fontSize: '1rem',
              color: '#6c757d',
              margin: 0,
            }}>
              Interactive inputs that show real-time system viability and topology recommendations
            </p>
          </div>
          
          <DiscoveryHub
            onPhysicsUpdate={handlePhysicsUpdate}
            onTopologyUpdate={handleTopologyUpdate}
            initialMainsPressure={2.5}
            initialFlowRate={18}
            initialRadiatorCount={8}
            initialPropertyFootprint={120}
          />
        </div>
      </section>

      {/* Physics & Topology Data Display */}
      {(physicsData || topologyData) && (
        <section style={{ 
          maxWidth: '1400px',
          margin: '0 auto 3rem auto',
        }}>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '1.5rem',
          }}>
            {/* Physics Data */}
            {physicsData && (
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                }}>
                  Physics Data
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#495057', lineHeight: '1.8' }}>
                  <div><strong>Mains Pressure:</strong> {physicsData.mainsPressure.toFixed(1)} bar</div>
                  <div><strong>Flow Rate:</strong> {physicsData.flowRate.toFixed(1)} L/min</div>
                  <div><strong>Combi Viable:</strong> {physicsData.combiViable ? '✅ Yes' : '❌ No'}</div>
                  <div><strong>Unvented Viable:</strong> {physicsData.unventedViable ? '✅ Yes' : '❌ No'}</div>
                </div>
              </div>
            )}

            {/* Topology Data */}
            {topologyData && (
              <div style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '1.5rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              }}>
                <h3 style={{ 
                  fontSize: '1.25rem',
                  fontWeight: 600,
                  marginBottom: '1rem',
                }}>
                  Topology Data
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#495057', lineHeight: '1.8' }}>
                  <div><strong>Radiator Count:</strong> {topologyData.radiatorCount}</div>
                  <div><strong>Property Size:</strong> {topologyData.propertyFootprint} m²</div>
                  <div><strong>Heat Loss:</strong> {topologyData.estimatedHeatLossKw} kW</div>
                  <div><strong>Recommended Plan:</strong> {topologyData.recommendedPlan}</div>
                  <div><strong>Plan Label:</strong> {topologyData.planLabel}</div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Phase 2: Baseline vs Enhanced */}
      <section style={{ 
        maxWidth: '1400px',
        margin: '0 auto 3rem auto',
      }}>
        <div style={{ 
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '1.75rem',
              fontWeight: 700,
              marginBottom: '0.5rem',
              color: '#212529',
            }}>
              Phase 2: Baseline vs Enhanced
            </h2>
            <p style={{ 
              fontSize: '1rem',
              color: '#6c757d',
              margin: 0,
            }}>
              "No-Brainer" presentation comparing essential compliance vs system transformation
            </p>
          </div>
          
          <BaselineVsEnhanced
            baselineOption={{
              title: 'Essential Compliance',
              subtitle: 'The boiler, standard flue, and basic connection',
              label: 'Standard',
              boilerName: 'Viessmann Vitodens 100-W 19kW',
              confidenceScore: 60,
              risks: [
                'Existing sludge remains a threat to warranty',
                'Old pipework may cause flow restrictions',
                'No system protection against debris',
                'Weather compensation not included',
                'Limited control over heating zones',
              ],
              warranties: [
                'Standard 5-year manufacturer warranty',
                'No extended coverage',
              ],
              styling: 'baseline',
            }}
            enhancedOption={{
              title: 'System Transformation',
              subtitle: 'Boiler + Filter + Flush + Weather Comp + Zoning',
              label: 'Premium',
              boilerName: 'Viessmann Vitodens 100-W 19kW',
              includesFilter: true,
              includesFlush: true,
              includesControls: true,
              includesZoning: true,
              confidenceScore: 98,
              benefits: [
                'System fully flushed and protected against debris',
                'Laminar flow filter prevents warranty voiding',
                'Weather compensation optimizes efficiency',
                'Advanced zoning maximizes comfort',
                'Full manufacturer warranty protection',
                '"Bulletproof" installation confidence',
              ],
              warranties: [
                'Extended 10-year manufacturer warranty',
                'Full parts and labor coverage',
                'Priority service support',
              ],
              styling: 'enhanced',
            }}
            onSelectOption={setSelectedOption}
            selectedOption={selectedOption}
          />
        </div>
      </section>

      {/* Phase 3: Recommendation Logic & Printed Presentation */}
      <section style={{ 
        maxWidth: '1400px',
        margin: '0 auto 3rem auto',
      }}>
        <div style={{ 
          backgroundColor: '#fff',
          borderRadius: '12px',
          padding: '2rem',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <h2 style={{ 
              fontSize: '1.75rem',
              fontWeight: 700,
              marginBottom: '0.5rem',
              color: '#212529',
            }}>
              Phase 3: Recommendation Logic & PDF Preview
            </h2>
            <p style={{ 
              fontSize: '1rem',
              color: '#6c757d',
              margin: 0,
            }}>
              UI-Shim wraps engine output with A/A-Enhanced logic and PDF formatting
            </p>
          </div>
          
          <RecommendationLogic
            engineOutput={sampleEngineOutput}
            onEnhanced={handleEnhancedUpdate}
          >
            {(enhanced) => (
              <div>
                {/* Engine Output Summary */}
                <div style={{
                  padding: '1.5rem',
                  backgroundColor: '#e7f3ff',
                  borderRadius: '8px',
                  border: '1px solid #007bff',
                  marginBottom: '2rem',
                }}>
                  <h3 style={{ 
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    marginBottom: '1rem',
                  }}>
                    Enhanced Recommendation Summary
                  </h3>
                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '1rem',
                    fontSize: '0.875rem',
                  }}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Topology</div>
                      <div>{enhanced.topologyPlan}</div>
                      <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                        {enhanced.topologyLabel}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Baseline Scope</div>
                      <div>Essential Only</div>
                      <div style={{ color: '#6c757d', fontSize: '0.75rem' }}>
                        {enhanced.baseline.confidenceScore}% confidence
                      </div>
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Enhanced Scope</div>
                      <div>Full System</div>
                      <div style={{ color: '#28a745', fontSize: '0.75rem' }}>
                        {enhanced.enhanced.confidenceScore}% confidence
                      </div>
                    </div>
                  </div>
                </div>

                {/* PDF Preview */}
                <div style={{
                  border: '2px solid #dee2e6',
                  borderRadius: '8px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#f8f9fa',
                    borderBottom: '1px solid #dee2e6',
                    fontWeight: 600,
                  }}>
                    PDF Preview (Print-Ready Format)
                  </div>
                  <PrintedPresentation
                    recommendation={enhanced}
                    showBeforeAfter={true}
                    printMode="full"
                  />
                </div>
              </div>
            )}
          </RecommendationLogic>
        </div>
      </section>

      {/* Footer with Back Link */}
      <div style={{ 
        textAlign: 'center',
        marginTop: '3rem',
        paddingTop: '2rem',
        borderTop: '1px solid #dee2e6',
      }}>
        <a 
          href="/"
          style={{ 
            color: '#007bff',
            textDecoration: 'none',
            fontSize: '1rem',
            fontWeight: 600,
          }}
        >
          ← Back to Home
        </a>
      </div>
    </div>
  );
};

export default DiscoveryHubDemo;
